import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Divider,
  Tab,
  Tabs,
  Collapse,
  Badge,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  LocalShipping,
  Inventory,
  VerifiedUser,
  Error as ErrorIcon,
  Info,
  ArrowBack,
  PlayArrow,
  Done,
  Warning,
  QrCodeScanner,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { DataService } from '../../services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import Loader from 'src/components/Loader';
import StatusCard from 'src/components/StatusCard';
import {
  TransferOrderStatus,
  TransferOrderRecordStatus,
  getTransferOrderStatusLabel,
} from 'src/types/TransferOrder.types';

// Helper function to read API response
async function readData<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
  }
  const body = await res.json().catch(() => null);

  if (body && typeof body === "object" && "data" in body) {
    if ("success" in body && body.success === false) {
      const msg = typeof body.message === "string" && body.message.trim()
        ? body.message
        : "Request failed";
      throw new Error(msg);
    }
    return body.data as T;
  }
  return body as T;
}

// Interfaces
interface TransferOrderForValidation {
  id: string;
  name: string;
  clientId: string;
  clientName: string;
  status: TransferOrderStatus;
  receptionStorageUnitId: string | null;
  receptionStorageUnitName: string | null;
  actualStorageUnitId: string | null;
  actualStorageUnitName: string | null;
  records: TransferOrderRecordForValidation[];
  notes: string | null;
  createdAt: string;
  shippedDate: string | null;
}

interface TransferOrderRecordForValidation {
  id: string;
  barcode: string;
  unitId: string | null;
  itemName: string | null;
  shippedQuantity: number;
  validatedQuantity: number;
  availableInReception: number;
  boxRefNumber: string | null;
  boxBreakdown: BoxInventoryBreakdown[] | null;
  status: TransferOrderRecordStatus;
  discrepancyQuantity: number | null;
  notes: string | null;
}

interface BoxInventoryBreakdown {
  boxRefNumber: string;
  quantity: number;
}

interface ValidateItem {
  barcode: string;
  quantity: number;
  sourceBoxRefNumber?: string;
  destinationBoxRefNumber?: string;
  notes?: string;
}

interface ValidationItemState extends TransferOrderRecordForValidation {
  quantityToValidate: number;
  selectedSourceBox: string;
  selectedDestinationBox: string;
  validationNotes: string;
}

const OrderReceivingDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const scannerRef = useRef<HTMLInputElement>(null);

  // Main state
  const [order, setOrder] = useState<TransferOrderForValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);

  // Validation state
  const [validationItems, setValidationItems] = useState<ValidationItemState[]>([]);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Scanner state
  const [scannerInput, setScannerInput] = useState('');
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');

  // Load order data for validation
  const loadOrderForValidation = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await DataService.get(`api/transferorder/${id}/for-validation`);
      if (!response.ok) {
        throw new Error('Failed to load transfer order');
      }

      const data = await readData<TransferOrderForValidation>(response);
      setOrder(data);

      // Initialize validation items
      const items: ValidationItemState[] = data.records.map(record => ({
        ...record,
        quantityToValidate: 0,
        selectedSourceBox: record.boxRefNumber || '',
        selectedDestinationBox: '',
        validationNotes: '',
      }));
      setValidationItems(items);

    } catch (err: any) {
      setError(err.message || 'Failed to load order');
      dispatch(
        showAlert({
          message: err.message || 'Failed to load order',
          type: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  }, [id, dispatch]);

  useEffect(() => {
    loadOrderForValidation();
  }, [loadOrderForValidation]);

  // Set active step based on order status
  useEffect(() => {
    if (!order) return;

    switch (order.status) {
      case TransferOrderStatus.InTransit:
        setActiveStep(0);
        break;
      case TransferOrderStatus.Received:
        setActiveStep(1);
        break;
      case TransferOrderStatus.Validating:
        setActiveStep(2);
        break;
      case TransferOrderStatus.Completed:
        setActiveStep(3);
        break;
      default:
        setActiveStep(0);
    }
  }, [order?.status]);

  // Auto-focus scanner when tab changes to items
  useEffect(() => {
    if (currentTab === 0 && scannerRef.current) {
      const timer = setTimeout(() => {
        scannerRef.current?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [currentTab]);

  // Keyboard shortcut - Ctrl+I to focus scanner
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        scannerRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Handle barcode scan
  const handleBarcodeScan = (scannedBarcode: string) => {
    const trimmedBarcode = scannedBarcode.trim();
    if (!trimmedBarcode) return;

    setLastScannedBarcode(trimmedBarcode);

    // Find the item with this barcode
    const itemIndex = validationItems.findIndex(item => item.barcode === trimmedBarcode);

    if (itemIndex === -1) {
      dispatch(
        showAlert({
          message: `Item ${trimmedBarcode} not found in this order`,
          type: 'error'
        })
      );
      return;
    }

    const item = validationItems[itemIndex];

    if (item.availableInReception === 0) {
      dispatch(
        showAlert({
          message: `No available quantity for ${trimmedBarcode}`,
          type: 'warning'
        })
      );
      return;
    }

    // Increment quantity to validate by 1
    setValidationItems(prev => {
      const updated = [...prev];
      const currentQty = updated[itemIndex].quantityToValidate;
      const maxQty = updated[itemIndex].availableInReception;

      if (currentQty < maxQty) {
        updated[itemIndex].quantityToValidate = currentQty + 1;
        dispatch(
          showAlert({
            message: `${trimmedBarcode}: ${currentQty + 1} / ${maxQty}`,
            type: 'success'
          })
        );
      } else {
        dispatch(
          showAlert({
            message: `${trimmedBarcode} already at maximum quantity (${maxQty})`,
            type: 'info'
          })
        );
      }

      return updated;
    });

    // Clear scanner input
    setScannerInput('');
  };

  // Handle scanner input
  const handleScannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScannerInput(e.target.value);
  };

  const handleScannerKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeScan(scannerInput);
    }
  };

  // Handle receive button click
  const handleReceiveOrder = async () => {
    if (!order) return;

    setProcessing(true);
    try {
      const response = await DataService.patch(
        `api/transferorder/${order.id}/status`,
        { status: TransferOrderStatus.Received }
      );

      if (!response.ok) {
        throw new Error('Failed to receive order');
      }

      dispatch(
        showAlert({
          message: 'Order received successfully! You can now start validating items.',
          type: 'success'
        })
      );

      await loadOrderForValidation();
      setReceiveDialogOpen(false);
    } catch (err: any) {
      dispatch(
        showAlert({
          message: err.message || 'Failed to receive order',
          type: 'error'
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  // Handle validation submission
  const handleValidateItems = async () => {
    if (!order) return;

    const itemsToValidate = validationItems.filter(item => item.quantityToValidate > 0);

    if (itemsToValidate.length === 0) {
      dispatch(
        showAlert({
          message: 'Please scan or enter quantities to validate',
          type: 'warning'
        })
      );
      return;
    }

    setProcessing(true);
    try {
      const validateItems: ValidateItem[] = itemsToValidate.map(item => ({
        barcode: item.barcode,
        quantity: item.quantityToValidate,
        sourceBoxRefNumber: item.selectedSourceBox || undefined,
        destinationBoxRefNumber: item.selectedDestinationBox || undefined,
        notes: item.validationNotes || undefined,
      }));

      const payload = {
        transferOrderId: order.id,
        items: validateItems,
        notes: 'Validated by client'
      };

      const response = await DataService.post('api/transferorder/validate', payload);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to validate items');
      }

      const result = await readData<any>(response);

      dispatch(
        showAlert({
          message: result.message || 'Items validated successfully!',
          type: 'success'
        })
      );

      await loadOrderForValidation();
    } catch (err: any) {
      dispatch(
        showAlert({
          message: err.message || 'Failed to validate items',
          type: 'error'
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  // Toggle item expansion
  const toggleItemExpansion = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Update validation item quantity
  const handleQuantityChange = (index: number, quantity: number) => {
    setValidationItems(prev => {
      const updated = [...prev];
      updated[index].quantityToValidate = Math.max(0, Math.min(quantity, updated[index].availableInReception));
      return updated;
    });
  };

  // Update source box selection
  const handleSourceBoxChange = (index: number, boxRef: string) => {
    setValidationItems(prev => {
      const updated = [...prev];
      updated[index].selectedSourceBox = boxRef;
      return updated;
    });
  };

  // Update destination box
  const handleDestinationBoxChange = (index: number, boxRef: string) => {
    setValidationItems(prev => {
      const updated = [...prev];
      updated[index].selectedDestinationBox = boxRef;
      return updated;
    });
  };

  // Update notes
  const handleNotesChange = (index: number, notes: string) => {
    setValidationItems(prev => {
      const updated = [...prev];
      updated[index].validationNotes = notes;
      return updated;
    });
  };

  // Calculate progress metrics
  const progressMetrics = useMemo(() => {
    if (!order) return null;

    const totalItems = order.records.length;
    const totalShipped = order.records.reduce((sum, r) => sum + r.shippedQuantity, 0);
    const totalValidated = order.records.reduce((sum, r) => sum + r.validatedQuantity, 0);
    const totalAvailable = order.records.reduce((sum, r) => sum + r.availableInReception, 0);

    const itemsFullyValidated = order.records.filter(r => r.validatedQuantity === r.shippedQuantity).length;
    const itemsPartiallyValidated = order.records.filter(r => r.validatedQuantity > 0 && r.validatedQuantity < r.shippedQuantity).length;
    const itemsPending = order.records.filter(r => r.validatedQuantity === 0).length;

    const discrepancies = order.records.filter(r => r.discrepancyQuantity && r.discrepancyQuantity !== 0).length;

    return {
      totalItems,
      totalShipped,
      totalValidated,
      totalAvailable,
      itemsFullyValidated,
      itemsPartiallyValidated,
      itemsPending,
      discrepancies,
      completionPercentage: totalShipped > 0 ? (totalValidated / totalShipped) * 100 : 0,
    };
  }, [order]);

  // Status configuration
  const statusConfig = useMemo(() => {
    if (!order) return null;

    const statusConfigs: Record<TransferOrderStatus, any> = {
      [TransferOrderStatus.New]: {
        color: 'info' as const,
        icon: <Schedule />,
        label: 'New'
      },
      [TransferOrderStatus.Allocated]: {
        color: 'primary' as const,
        icon: <Inventory />,
        label: 'Allocated'
      },
      [TransferOrderStatus.Picking]: {
        color: 'warning' as const,
        icon: <Schedule />,
        label: 'Picking'
      },
      [TransferOrderStatus.Ready]: {
        color: 'secondary' as const,
        icon: <CheckCircle />,
        label: 'Ready'
      },
      [TransferOrderStatus.InTransit]: {
        color: 'warning' as const,
        icon: <LocalShipping />,
        label: 'In Transit'
      },
      [TransferOrderStatus.Received]: {
        color: 'success' as const,
        icon: <CheckCircle />,
        label: 'Received'
      },
      [TransferOrderStatus.Validating]: {
        color: 'info' as const,
        icon: <VerifiedUser />,
        label: 'Validating'
      },
      [TransferOrderStatus.Completed]: {
        color: 'success' as const,
        icon: <Done />,
        label: 'Completed'
      },
    };

    return statusConfigs[order.status] || {
      color: 'default' as const,
      icon: <Info />,
      label: getTransferOrderStatusLabel(order.status)
    };
  }, [order?.status]);

  // Get discrepancy config
  const getDiscrepancyConfig = (item: ValidationItemState) => {
    const diff = item.validatedQuantity - item.shippedQuantity;

    if (diff === 0 && item.validatedQuantity === item.shippedQuantity) {
      return {
        icon: <CheckCircle fontSize="small" />,
        label: 'Match',
        bgcolor: '#e8f5e9',
        textColor: '#2e7d32'
      };
    } else if (diff > 0) {
      return {
        icon: <Warning fontSize="small" />,
        label: 'Overage',
        bgcolor: '#fff3e0',
        textColor: '#f57c00'
      };
    } else if (diff < 0 && item.validatedQuantity > 0) {
      return {
        icon: <ErrorIcon fontSize="small" />,
        label: 'Shortage',
        bgcolor: '#ffebee',
        textColor: '#c62828'
      };
    } else {
      return {
        icon: <Schedule fontSize="small" />,
        label: 'Pending',
        bgcolor: '#f5f5f5',
        textColor: '#757575'
      };
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error || !order) {
    return (
      <StatusCard
        layout="page"
        variant="not-found"
        imageSrc={process.env.PUBLIC_URL + '/static/images/status/error1.svg'}
        title="Order Not Found"
        message="Sorry, this transfer order doesn't exist or is not ready for receiving."
        actionLabel="Back to Orders"
        onAction={() => navigate('/order-receiving')}
      />
    );
  }

  const canReceive = order.status === TransferOrderStatus.InTransit;
  const canValidate = order.status === TransferOrderStatus.Received || order.status === TransferOrderStatus.Validating;
  const isCompleted = order.status === TransferOrderStatus.Completed;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header - Matching Shipment Style */}
      <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={(theme) => ({
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            p: { xs: 2, md: 3 },
          })}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            {/* LEFT SIDE */}
            <Grid item xs={12} md={6}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                <IconButton
                  onClick={() => navigate('/order-receiving')}
                  color="inherit"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>
                  Order Validation
                </Typography>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.05rem', fontWeight: 500 }}>
                  {order.name}
                </Typography>

                {statusConfig && (
                  <Chip
                    icon={statusConfig.icon}
                    label={statusConfig.label}
                    color={statusConfig.color}
                    variant="filled"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Stack>
            </Grid>

            {/* RIGHT SIDE - Action Buttons */}
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                {canReceive && (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={() => setReceiveDialogOpen(true)}
                    disabled={processing}
                    size="large"
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    Receive Order
                  </Button>
                )}
                {canValidate && (
                  <Button
                    variant="contained"
                    startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
                    onClick={handleValidateItems}
                    disabled={processing}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    {processing ? 'Validating...' : 'Validate Items'}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {['In Transit', 'Received', 'Validating', 'Completed'].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Summary Cards - Compact */}
      {progressMetrics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Total Items</Typography>
                <Typography variant="h5" fontWeight={700}>{progressMetrics.totalItems}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Shipped Qty</Typography>
                <Typography variant="h5" fontWeight={700}>{progressMetrics.totalShipped}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Validated Qty</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">{progressMetrics.totalValidated}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" color="text.secondary">Discrepancies</Typography>
                <Typography variant="h5" fontWeight={700} color={progressMetrics.discrepancies > 0 ? 'error.main' : 'success.main'}>
                  {progressMetrics.discrepancies}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Status Alerts - Compact */}
      {canReceive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Order ready for receiving. Click "Receive Order" to start validation.
        </Alert>
      )}

      {canValidate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ready for validation. Scan items or enter quantities manually below.
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Items" />
          <Tab label="Order Info" />
        </Tabs>

        {/* Items Tab */}
        {currentTab === 0 && (
          <CardContent sx={{ p: 2 }}>
            {/* Barcode Scanner */}
            {canValidate && (
              <Card sx={{ mb: 2, bgcolor: 'grey.50' }} variant="outlined">
                <CardHeader
                  avatar={<QrCodeScanner color="primary" />}
                  title="Barcode Scanner"
                  subheader="Scan items to automatically increase quantity (Ctrl+I to focus)"
                  sx={{ p: 2, '& .MuiCardHeader-title': { fontSize: '1rem' }, '& .MuiCardHeader-subheader': { fontSize: '0.8rem' } }}
                />
                <Divider />
                <CardContent sx={{ p: 2 }}>
                  <TextField
                    inputRef={scannerRef}
                    fullWidth
                    size="small"
                    placeholder="Focus here and scan barcode..."
                    value={scannerInput}
                    onChange={handleScannerChange}
                    onKeyPress={handleScannerKeyPress}
                    autoFocus
                    InputProps={{
                      startAdornment: <QrCodeScanner sx={{ mr: 1, color: 'action.active' }} />,
                    }}
                    helperText={lastScannedBarcode ? `Last scanned: ${lastScannedBarcode}` : null}
                  />
                </CardContent>
              </Card>
            )}

            {/* Items Table - Compact */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Barcode</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="center">Shipped</TableCell>
                    <TableCell align="center">Validated</TableCell>
                    <TableCell align="center">Available</TableCell>
                    {canValidate && (
                      <>
                        <TableCell align="center">To Validate</TableCell>
                        <TableCell>Source Box</TableCell>
                        <TableCell>Dest Box</TableCell>
                      </>
                    )}
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validationItems.map((item, index) => {
                    const discrepancyConfig = getDiscrepancyConfig(item);
                    return (
                      <React.Fragment key={item.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleItemExpansion(item.id)}>
                              {expandedItems.has(item.id) ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace" fontWeight={500} fontSize="0.875rem">
                              {item.barcode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontSize="0.875rem">{item.itemName || '-'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontSize="0.875rem">{item.shippedQuantity}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600} fontSize="0.875rem" color={item.validatedQuantity === item.shippedQuantity ? 'success.main' : 'text.primary'}>
                              {item.validatedQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontSize="0.875rem">{item.availableInReception}</Typography>
                          </TableCell>
                          {canValidate && (
                            <>
                              <TableCell align="center">
                                <Badge badgeContent={item.quantityToValidate > 0 ? item.quantityToValidate : null} color="primary" max={999}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={item.quantityToValidate}
                                    onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                                    inputProps={{
                                      min: 0,
                                      max: item.availableInReception,
                                      style: { textAlign: 'center', width: 50, fontSize: '0.875rem' }
                                    }}
                                    disabled={!canValidate || item.availableInReception === 0}
                                  />
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  select
                                  value={item.selectedSourceBox}
                                  onChange={(e) => handleSourceBoxChange(index, e.target.value)}
                                  SelectProps={{ native: true }}
                                  sx={{ minWidth: 90, fontSize: '0.875rem' }}
                                  disabled={!canValidate || !item.boxBreakdown}
                                >
                                  <option value="">Auto</option>
                                  {item.boxBreakdown?.map(box => (
                                    <option key={box.boxRefNumber} value={box.boxRefNumber}>
                                      {box.boxRefNumber}
                                    </option>
                                  ))}
                                </TextField>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  placeholder="Box"
                                  value={item.selectedDestinationBox}
                                  onChange={(e) => handleDestinationBoxChange(index, e.target.value)}
                                  sx={{ width: 90 }}
                                  disabled={!canValidate}
                                  inputProps={{ style: { fontSize: '0.875rem' } }}
                                />
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <Chip
                              icon={discrepancyConfig.icon}
                              label={discrepancyConfig.label}
                              size="small"
                              sx={{
                                bgcolor: discrepancyConfig.bgcolor,
                                color: discrepancyConfig.textColor,
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row */}
                        <TableRow>
                          <TableCell colSpan={11} sx={{ py: 0, borderBottom: expandedItems.has(item.id) ? 1 : 0 }}>
                            <Collapse in={expandedItems.has(item.id)}>
                              <Box sx={{ py: 2, pl: 6, bgcolor: 'grey.50' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">Unit ID</Typography>
                                    <Typography variant="body2" fontSize="0.875rem">{item.unitId || '-'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">Box Reference</Typography>
                                    <Typography variant="body2" fontSize="0.875rem">{item.boxRefNumber || 'Independent'}</Typography>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="caption" color="text.secondary">Discrepancy</Typography>
                                    <Typography variant="body2" fontSize="0.875rem" color={item.discrepancyQuantity ? 'error.main' : 'text.primary'}>
                                      {item.discrepancyQuantity || 'None'}
                                    </Typography>
                                  </Grid>
                                  {item.boxBreakdown && item.boxBreakdown.length > 0 && (
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="text.secondary">Box Breakdown</Typography>
                                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                                        {item.boxBreakdown.map(box => (
                                          <Chip
                                            key={box.boxRefNumber}
                                            label={`${box.boxRefNumber}: ${box.quantity}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontSize: '0.75rem' }}
                                          />
                                        ))}
                                      </Stack>
                                    </Grid>
                                  )}
                                  {canValidate && (
                                    <Grid item xs={12}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Validation notes..."
                                        value={item.validationNotes}
                                        onChange={(e) => handleNotesChange(index, e.target.value)}
                                        inputProps={{ style: { fontSize: '0.875rem' } }}
                                      />
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        )}

        {/* Order Info Tab */}
        {currentTab === 1 && (
          <CardContent sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Client</Typography>
                <Typography variant="body2" fontWeight={500}>{order.clientName}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Created At</Typography>
                <Typography variant="body2">{new Date(order.createdAt).toLocaleString()}</Typography>
              </Grid>
              {order.shippedDate && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Shipped At</Typography>
                  <Typography variant="body2">{new Date(order.shippedDate).toLocaleString()}</Typography>
                </Grid>
              )}
              {order.receptionStorageUnitName && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Reception Storage</Typography>
                  <Typography variant="body2">{order.receptionStorageUnitName}</Typography>
                </Grid>
              )}
              {order.actualStorageUnitName && (
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Actual Storage</Typography>
                  <Typography variant="body2">{order.actualStorageUnitName}</Typography>
                </Grid>
              )}
              {order.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{order.notes}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        )}
      </Card>

      {/* Receive Order Dialog */}
      <Dialog
        open={receiveDialogOpen}
        onClose={() => !processing && setReceiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Receive Order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Receiving this order will change its status to "Received" and allow you to start validating items.
            </Alert>
            <Typography>
              Are you sure you want to receive order <strong>{order.name}</strong>?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleReceiveOrder}
            variant="contained"
            color="primary"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : <PlayArrow />}
          >
            {processing ? 'Receiving...' : 'Receive Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderReceivingDetails;
